import { createReducer, on } from '@ngrx/store'
import {
  Capaign,
  CustomFieldValue,
  ManageSubscriptionState,
  SelectNewsOrThemen,
  StateStatus,
  UserData,
} from './manage-subscription.state'
import { manageSubscriptionActions } from './manage-subscription.actions'
import { createCustomFieldWith } from '../utils/custom-fields'
import { environment } from '../../../../environments/environment'
import { FilterCustomFieldsPipe } from '../pipes/filter-custom-fields.pipe'

const initialCapaignState: Capaign = {
  campaignId: '',
  href: '',
  name: '',
}

const initialStateStatus: StateStatus = {
  loading: true,
  error: false,
}

const initialUserData: UserData = {
  contactId: '',
  name: '',
  origin: '',
  timeZone: '',
  activities: '',
  changedOn: '',
  createdOn: '',
  campaign: initialCapaignState,
  email: '',
  dayOfCycle: '',
  scoring: 0,
  engagementScore: 0,
  href: '',
  note: '',
  ipAddress: '',
  customFieldValues: [],
}

const initialState: ManageSubscriptionState = {
  data: initialUserData,
  customFields: [],
  controls: { isOpen: [], isSelected: [], disableDoi: false, shouldShowUwg7: true, isUnsubscribed: false },
  status: initialStateStatus,
}

const createSelectionsBasedOnCustomFields = (
  customFields: CustomFieldValue[],
  userCustomFields: CustomFieldValue[],
): SelectNewsOrThemen[] => {
  return customFields.map(field => ({
    id: field.customFieldId,
    isSelected: userCustomFields.some(userField => userField.customFieldId === field.customFieldId),
    themenSelected: field.values.map(t => ({
      id: t,
      isSelected:
        userCustomFields.find(userField => userField.customFieldId === field.customFieldId)?.values.includes(t) ||
        false,
    })),
  }))
}

const checkUnsubscribed = (userCustomFields: CustomFieldValue[]): boolean => {
  const filterPipe = new FilterCustomFieldsPipe()
  const userDoiIds = [environment.doiId, environment.uwg7Id]
  const userCheckCustomFields = filterPipe.transform(userCustomFields)
  const userSubscriptions = userCheckCustomFields.filter(c => !userDoiIds.includes(c.customFieldId))
  const userDoi = userCheckCustomFields.find(c => c.customFieldId === environment.doiId)
  const userUwg7 = userCheckCustomFields.find(c => c.customFieldId === environment.uwg7Id)

  if (!(userCheckCustomFields.length > 0)) return true
  if (userSubscriptions.length > 0) return false
  if (userDoi && userUwg7 && userDoi.value[0] === 'false' && userUwg7.value[0] === 'false') return true
  if (userDoi && !userUwg7 && userDoi.value[0] === 'false') return true
  // if (!userDoi && userUwg7 && userUwg7.value[0] === 'false') return true
  return false
}

const getCustomFieldById = (
  userCustomFields: CustomFieldValue[],
  customFieldId: string,
): CustomFieldValue | undefined => {
  return userCustomFields.find(c => c.customFieldId === customFieldId)
}

export const manageSubscriptionReducer = createReducer(
  initialState,
  on(
    manageSubscriptionActions.tryLoadData,
    (state): ManageSubscriptionState => ({
      ...state,
      status: {
        loading: true,
        error: false,
        errorMessage: undefined,
      },
    }),
  ),
  on(manageSubscriptionActions.successLoadData, (state, action): ManageSubscriptionState => {
    const userCustomFields = action.payload?.customFieldValues || []
    const doiField = getCustomFieldById(userCustomFields, environment.doiId)
    const uwg7Field = getCustomFieldById(userCustomFields, environment.uwg7Id)

    return {
      ...state,
      data: action.payload ?? ({} as UserData),
      controls: {
        isOpen: Array.apply(false, Array(state.customFields.length)).fill(true) as boolean[],
        isSelected: createSelectionsBasedOnCustomFields(state.customFields, userCustomFields),
        disableDoi: !doiField || (!!doiField && doiField.value[0].toLowerCase() === 'false'),
        shouldShowUwg7: !!uwg7Field && uwg7Field.values[0].toLowerCase() === 'true',
        isUnsubscribed: checkUnsubscribed(userCustomFields),
      },
      status: {
        loading: false,
        error: false,
        errorMessage: undefined,
      },
    }
  }),
  on(
    manageSubscriptionActions.errorLoadData,
    (state, action): ManageSubscriptionState => ({
      ...state,
      controls: {
        ...state.controls,
        isUnsubscribed: true,
      },
      status: {
        loading: false,
        error: true,
        errorMessage: action.payload.error,
      },
    }),
  ),
  on(manageSubscriptionActions.successLoadCustomFields, (state, action): ManageSubscriptionState => {
    return {
      ...state,
      customFields: action.payload,
      status: {
        loading: false,
        error: false,
        errorMessage: undefined,
      },
    }
  }),
  on(
    manageSubscriptionActions.errorLoadCustomFields,
    (state, action): ManageSubscriptionState => ({
      ...state,
      status: {
        loading: false,
        error: true,
        errorMessage: action.payload.error,
      },
    }),
  ),
  on(manageSubscriptionActions.toggleOption, (state, action) => ({
    ...state,
    controls: {
      ...state.controls,
      isOpen: state.controls.isOpen.map((op, index) => {
        if (index === action.payload.index) return !op
        return op
      }),
    },
  })),
  on(manageSubscriptionActions.toggleAbsenderSelection, (state, action) => {
    return {
      ...state,
      controls: {
        ...state.controls,
        isSelected: state.controls.isSelected.map(v => {
          console.log(v.id === action.payload.aId)
          if (v.id === action.payload.aId)
            return {
              ...v,
              isSelected: !v.isSelected,
              themenSelected: v.themenSelected.map(t => ({ ...t, isSelected: !v.isSelected })),
            }
          return v
        }),
      },
    }
  }),
  on(manageSubscriptionActions.toggleThemenSelection, (state, action) => ({
    ...state,
    controls: {
      ...state.controls,
      isSelected: state.controls.isSelected.map(absender => {
        if (absender.id === action.payload.aId) {
          const newThemenStats = absender.themenSelected.map(themen => {
            if (themen.id === action.payload.tId) return { ...themen, isSelected: !themen.isSelected }
            return themen
          })
          return {
            ...absender,
            isSelected: newThemenStats.some(el => el.isSelected),
            themenSelected: newThemenStats,
          }
        }
        return absender
      }),
    },
  })),
  on(manageSubscriptionActions.toggleUserOrigin, (state, action) => {
    const userCustomField = state.data.customFieldValues.find(f => f.customFieldId === action.payload.customFieldId)
    if (!userCustomField)
      return {
        ...state,
        data: {
          ...state.data,
          customFieldValues: [...state.data.customFieldValues, createCustomFieldWith(action.payload.customFieldId)],
        },
      }

    return {
      ...state,
      data: {
        ...state.data,
        customFieldValues: state.data.customFieldValues.map(f =>
          f.customFieldId === action.payload.customFieldId
            ? {
                ...f,
                value: [f.value[0]?.toLowerCase() === 'true' ? 'FALSE' : 'TRUE'],
                values: [f.value[0]?.toLowerCase() === 'true' ? 'FALSE' : 'TRUE'],
              }
            : f,
        ),
      },
    }
  }),
  on(manageSubscriptionActions.unmarkAll, state => ({
    ...state,
    controls: {
      ...state.controls,
      isSelected: state.controls.isSelected.map(absender => {
        return {
          ...absender,
          isSelected: false,
          themenSelected: absender.themenSelected.map(t => ({
            ...t,
            isSelected: false,
          })),
        }
      }),
    },
    status: {
      ...state.status,
    },
  })),
  on(manageSubscriptionActions.unmarkUserDoi, state => {
    const ids = [environment.doiId, environment.uwg7Id]
    const userDoi = state.data.customFieldValues.find(c => c.customFieldId === environment.doiId)
    const userUwg7Id = state.data.customFieldValues.find(c => c.customFieldId === environment.uwg7Id)
    const newCustomFieldValues: CustomFieldValue[] = [
      ...state.data.customFieldValues.filter(c => !ids.includes(c.customFieldId)),
    ]

    if (userDoi) newCustomFieldValues.push({ ...userDoi, value: ['FALSE'], values: ['FALSE'] })
    if (userUwg7Id) newCustomFieldValues.push({ ...userUwg7Id, value: ['FALSE'], values: ['FALSE'] })
    return {
      ...state,
      data: {
        ...state.data,
        customFieldValues: newCustomFieldValues,
      },
      status: {
        ...state.status,
      },
    }
  }),
  on(
    manageSubscriptionActions.trySaveUserPref,
    (state): ManageSubscriptionState => ({
      ...state,
      status: {
        loading: true,
        error: false,
        errorMessage: undefined,
      },
    }),
  ),
  on(
    manageSubscriptionActions.saveUserPrefSuccess,
    (state): ManageSubscriptionState => ({
      ...state,
      status: {
        loading: false,
        error: false,
        errorMessage: undefined,
      },
    }),
  ),
  on(
    manageSubscriptionActions.saveUserPrefError,
    (state, action): ManageSubscriptionState => ({
      ...state,
      status: {
        loading: false,
        error: true,
        errorMessage: action.payload.error,
      },
    }),
  ),
)
